<template>
  <div>
    <building-permit-template />
    <h2 class="mt-4">{{ $t('volume-title') }}</h2>
    <p class="mb-4">{{ $t('volume-description') }}</p>
    <DatePicker v-if="start_date && end_date" v-on:set-year="handleYear" :from_date="start_date" :to_date="end_date" :set_year="selected_year" :optional_months="false"></DatePicker>
    <div class="accordion mt-4" id="report-qualifications">
      <div class="card">
    <div class="card-header" id="headingOne">
      <div class="heading" role="button" data-toggle="collapse" data-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
        {{ $t('report-qualifications') }}
        <i class="far fa-chevron-down"></i>
      </div>
    </div>
    <div id="collapseOne" class="collapse mb-4" aria-labelledby="headingOne">
      <div class="card-body">
        <ul>
          <li>{{ $t('permit-types-not-included') }}</li>
          <li>{{ $t('residential-permit-activity-description') }}
            <ul>
              <li>{{ $t('accessory-structure-includes') }}</li>
            </ul>
          </li>
          <li>{{ $t('commercial-permit-activity-description') }}</li>
        </ul>
        <p><strong>{{ $t('disclaimer') }}</strong><br /> {{ $t('disclaimer-text') }}</p>
        </div>
      </div>
    </div>      
    </div>
    <h3 v-if="hasYearParam" class="mt-4">{{ $t('permit-volume-for',{text_date: textDate}) }}</h3>
    <div v-if="hasYearParam">
        <permit-activity 
          :selected-year="selected_year" 
          :filter-function="housingFilter"
          :issues-filter-function="housingIssuesFilter"
          :permit-applications="dataByYear[selected_year]"
          :permits-issued="issuesByYear[selected_year]"
          graph-title="residential-permit-activity-chart-title"
          class="mt-4"
        />
        <permit-activity 
          :selected-year="selected_year" 
          :filter-function="commercialFilter"
          :issues-filter-function="commercialIssuesFilter"
          :permit-applications="dataByYear[selected_year]"
          :permits-issued="issuesByYear[selected_year]"
          graph-title="commercial-permit-activity-chart-title"
          class="mt-4"
        />

        <cumulative-housing-and-commercial 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          graph-title="cumulative-housing-commercial-chart-title"
          class="mt-4"
        />
        <cumulative-housing-and-commercial 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="housingFilter"
          graph-title="cumulative-housing-chart-title"
          class="mt-4"
        />
        <cumulative-housing-and-commercial 
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="commercialFilter"
          graph-title="cumulative-commercial-chart-title"
          class="mt-4"
        />
       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="housingNewAndAdditionsFilter"
          graph-title="housing-new-and-additions-chart-title"
          class="mt-4"
        />

       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="housingRenovationsFilter"
          graph-title="housing-renovations-chart-title"
          class="mt-4"
        />
        
       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="housingAccessoryStructuresFilter"
          graph-title="housing-accessory-structures-chart-title"
          class="mt-4"
        />

       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="housingFilter"
          graph-title="housing-all-requests-chart-title"
          class="mt-4"
        />


       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="commercialNewAndAdditionsFilter"
          graph-title="commercial-new-and-additions-chart-title"
          class="mt-4"
        />

       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="commercialRenovationsFilter"
          graph-title="commercial-renovations-chart-title"
          class="mt-4"
        />

       <activity-and-linear
          :selected-year="selected_year" 
          :data-by-year="dataByYear"
          :filter-function="commercialNewAndAdditionsAndRenovationsFilter"
          graph-title="commercial-new-and-additions-and-renovations-chart-title"
          class="mt-4"
        />
    </div>
  </div>
</template>

<script>

import DatePicker from '@/components/DatePicker';
import CumulativeHousingAndCommercial from '@/components/CumulativeHousingAndCommercial';
import PermitActivity from '@/components/PermitActivity';
import ActivityAndLinear from '@/components/ActivityAndLinear';
import axios from 'axios';
import BuildingPermitTemplate from '@/components/BuildingPermitTemplate';
export default {

  components: {
    DatePicker,
    CumulativeHousingAndCommercial,
    PermitActivity,
    ActivityAndLinear,
    BuildingPermitTemplate,
  },

  data() {
    return {
      start_date: null,
      end_date: null,
      selected_year: "",
      application_data: [],
      issues_data: [],
    }
  },

  async created() {
    if (this.$route.params.year) {
      this.selected_year = "" + this.$route.params.year;
    }
    const url = this.$PERMIT_VOLUME_API + "?$select=min(app_start_month) as start_date,max(app_start_month) as end_date";
    const { data } = await axios.get(url);
    if (data.length) {
      const minDate = new Date(data[0].start_date);
      this.start_date = new Date(minDate.setFullYear(minDate.getFullYear()));
      this.end_date = new Date(data[0].end_date);
    }
    this.fetchData();
  },

  computed: {

    hasYearParam() {
      return this.$route.params.year && this.$route.params.year.length > 0;
    },

    textDate() {
      if (!this.hasYearParam) return '';
      const cur_date = new Date(this.curYear, 0); // January of the selected year
      const options = { year: 'numeric' };
      return cur_date.toLocaleDateString('en-CA', options);
    },

    curYear() {
      return this.$route.params.year ? this.$route.params.year + "" : "";
    },

    dataByYear() {
      let dataByYear = {};

      this.application_data.forEach(item => {
        let start_date = new Date(item.app_start_month);
        const year = start_date.getFullYear();
        if (!dataByYear[year]) {
          dataByYear[year] = [];
        }
        dataByYear[year].push(item);
      });

      return dataByYear;
    },
    issuesByYear() {
      let issuesByYear = {};
      this.issues_data.forEach(item => {
        let start_date = new Date(item.app_start_month);
        const year = start_date.getFullYear();
        if (!issuesByYear[year]) {
          issuesByYear[year] = [];
        }
        issuesByYear[year].push(item);
      });
      return issuesByYear;
    },

  },

  methods: {
    handleYear(value) {
      if (value) {
        this.selected_year = "" + value;
      } else {
        this.selected_year = "";
      }
      this.handleDate();
    },

    handleDate() {
      const targetRoute = this.selected_year 
        ? { name: 'volume', params: { year: this.selected_year } }
        : { name: 'volume' };
      
      // Check if we're already on the target route to avoid redundant navigation
      const currentRoute = this.$route;
      const isSameRoute = currentRoute.name === targetRoute.name && 
        currentRoute.params.year === targetRoute.params?.year;
      
      if (!isSameRoute) {
        this.$router.replace(targetRoute);
      }
    },

    async fetchData() {
      const url = this.$PERMIT_VOLUME_API + "?$limit=10000&$order=app_start_month asc";
      const { data } = await axios.get(url);
      if (data.length) {
        this.application_data = data;
      }
      const issues_url = this.$PERMIT_DETAILS_API + `?$limit=10000&$select=date_trunc_ym(issue_date) as app_start_month, permit_type, count(*) as total_count&$group=date_trunc_ym(issue_date), permit_type&$order=date_trunc_ym(issue_date), permit_type&$where=permit_type != "Sign" AND permit_type != "Temporary Building Renewals" AND permit_type != "TBD - Commercial"`;
      const issues_data = await axios.get(issues_url);
      if (issues_data.data.length) {
        this.issues_data = issues_data.data;
      }else{
        this.issues_data = [];
      }
    },


    commercialFilter(item) {
      return item.category && item.category.toLowerCase().includes('commercial');
    },

    housingFilter(item) {
      return item.category && item.category.toLowerCase().includes('residential');
    },

    housingNewAndAdditionsFilter(item) {
      return item.category && item.category.toLowerCase().includes('residential') && (item.application_type.toLowerCase().includes('addition') || item.application_type.toLowerCase().includes('new')); 
    },

    housingRenovationsFilter(item) {
      return item.category && item.category.toLowerCase().includes('residential') && item.application_type.toLowerCase().includes('reno'); 
    },

    housingAccessoryStructuresFilter(item) {
      return item.category && item.category.toLowerCase().includes('residential') && item.sub_category.toLowerCase().includes('accessory structure'); 
    },

    commercialNewAndAdditionsFilter(item) {
      return item.category && item.category.toLowerCase().includes('commercial') && (item.application_type.toLowerCase().includes('addition') || item.application_type.toLowerCase().includes('new')); 
    },

    commercialRenovationsFilter(item) {
      return item.category && item.category.toLowerCase().includes('commercial') && item.application_type.toLowerCase().includes('reno'); 
    },

    commercialNewAndAdditionsAndRenovationsFilter(item) {
      return item.category && item.category.toLowerCase().includes('commercial') && (item.application_type.toLowerCase().includes('addition') || item.application_type.toLowerCase().includes('new') || item.application_type.toLowerCase().includes('reno')); 
    },

    commercialIssuesFilter(item) {
      return item.permit_type && !item.permit_type.toLowerCase().includes('housing') && !item.permit_type.toLowerCase().includes('accessory structures');
    },

    housingIssuesFilter(item) {
      return item.permit_type && item.permit_type.toLowerCase().includes('housing') || item.permit_type.toLowerCase().includes('accessory structures');
    },

  }

}

</script>
